import { Dayjs } from 'dayjs';
import {
  BusinessModuleRef, DataModel, DataModelBuilder, fromEnumAsString, isSameData,
  MapUtils, SerializationContext, SerializedData, TimeZone, TimeZoneRegion, toEnumFromString,
} from 'in-time-core';

export class PromoEventData extends DataModel {
  static readonly TYPE_ID: string = '7f29a7447a06413d9206bb58a92cb4cf';

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly timezone: TimeZoneRegion,
    public readonly eventId: string,
    public readonly eventName: string,
    public readonly eventWebRoute: string,
    public readonly eventStartDate: Dayjs | null,
    public readonly eventEndDate: Dayjs | null,
    public readonly eventCoverDownloadUrl: string,
    public readonly ticketId: string,
    public readonly isPurchasable: boolean,
    public readonly isSoldOut: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return PromoEventData.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): PromoEventData {
    return new PromoEventData(
      BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      toEnumFromString(MapUtils.getString(map, 'timezone'), TimeZoneRegion),
      MapUtils.getString(map, 'eventId'),
      MapUtils.getString(map, 'eventName'),
      MapUtils.getString(map, 'eventWebRoute'),
      TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'eventStartDate')),
      TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'eventEndDate')),
      MapUtils.getString(map, 'eventCoverDownloadUrl'),
      MapUtils.getString(map, 'ticketId'),
      MapUtils.getBoolean(map, 'isPurchasable'),
      MapUtils.getBoolean(map, 'isSoldOut'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'timezone': fromEnumAsString(this.timezone),
      'eventId': this.eventId,
      'eventName': this.eventName,
      'eventWebRoute': this.eventWebRoute,
      'eventStartDate': TimeZone.encodeDateOrNull(this.eventStartDate),
      'eventEndDate': TimeZone.encodeDateOrNull(this.eventEndDate),
      'eventCoverDownloadUrl': this.eventCoverDownloadUrl,
      'ticketId': this.ticketId,
      'isPurchasable': this.isPurchasable,
      'isSoldOut': this.isSoldOut,
    };
  }

  clone(): this {
    return new PromoEventData(
      this.businessRef.clone(),
      this.timezone,
      this.eventId,
      this.eventName,
      this.eventWebRoute,
      TimeZone.cloneDateOrNull(this.eventStartDate),
      TimeZone.cloneDateOrNull(this.eventEndDate),
      this.eventCoverDownloadUrl,
      this.ticketId,
      this.isPurchasable,
      this.isSoldOut,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.timezone === this.timezone &&
      other.ticketId === this.ticketId &&
      other.isPurchasable === this.isPurchasable &&
      other.isSoldOut === this.isSoldOut &&
      other.eventId === this.eventId &&
      other.eventName === this.eventName &&
      other.eventWebRoute === this.eventWebRoute &&
      other.eventStartDate === this.eventStartDate &&
      other.eventEndDate === this.eventEndDate &&
      other.eventCoverDownloadUrl === this.eventCoverDownloadUrl;
  }
}

export class PromoEventDataBuilder extends DataModelBuilder<PromoEventData> {
  build(context: SerializationContext, map: SerializedData): PromoEventData {
    return PromoEventData.fromMap(context, map);
  }
}
